import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const TermsPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#161513] py-12 px-4 sm:px-6">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url('/src/assets/logo.gif')` }}
      />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center mb-8">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </div>
        
        <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-[#0CB4E4]/20">
            <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
            <p className="text-gray-300 mt-2">Effective Date: November 1, 2025</p>
            <p className="text-gray-300 mt-4">
              By using cr3ate.ai, you agree to these Terms and the{' '}
              <Link to="/privacy" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                Privacy Policy
              </Link>
              . If you disagree, do not use the Service.
            </p>
          </div>
          
          <div className="p-6 md:p-8 space-y-6">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. Eligibility</h2>
              <p className="text-gray-300">
                You must be at least 13 years old (or digital age of consent in your jurisdiction). 
                You are responsible for all activity under your account.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. Use of Service</h2>
              <p className="text-gray-300">
                The Service allows uploading images or videos to generate 3D reconstructions using 
                proprietary or evolving rendering technologies. We may modify or discontinue features 
                at any time.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Free Tier</h2>
              <p className="text-gray-300">
                The free version of cr3ate.ai is for personal, non-commercial use only, unless 
                explicitly authorized in writing by Ydrive Inc.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Paid Access</h2>
              <p className="text-gray-300">
                Subscriptions, tokens, or enterprise licenses grant commercial use rights under 
                applicable plans. Payments are non-refundable except where required by law.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Ownership</h2>
              <p className="text-gray-300">
                You retain rights to your uploads. By submitting content, you grant Ydrive Inc. 
                a limited license to process, improve, and train models as described in the Privacy Policy.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. Account Inactivity</h2>
              <p className="text-gray-300">
                Inactive accounts (6+ months) may be deleted, including all related data.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. Prohibited Conduct</h2>
              <ul className="list-disc pl-5 text-gray-300 space-y-2">
                <li>Violate laws or rights of others</li>
                <li>Upload illegal, harmful, or infringing material</li>
                <li>Use bots, scrapers, or automated systems</li>
                <li>Reverse-engineer or misuse our software</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Intellectual Property</h2>
              <p className="text-gray-300">
                All software, code, and branding remain property of Ydrive Inc. You may not copy, 
                modify, or distribute without written consent.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">9. Disclaimer</h2>
              <p className="text-gray-300">
                The Service is provided "AS IS." We do not guarantee accuracy or uptime.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">10. Limitation of Liability</h2>
              <p className="text-gray-300">
                Ydrive Inc. shall not be liable for indirect, incidental, or consequential damages 
                exceeding the amount paid in the prior 12 months.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">11. Termination</h2>
              <p className="text-gray-300">
                We may terminate accounts violating these Terms. You may terminate anytime by 
                emailing{' '}
                <a href="mailto:support@cr3ate.ai" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  support@cr3ate.ai
                </a>.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">12. Governing Law</h2>
              <p className="text-gray-300">
                These Terms are governed by Delaware law. EU/UK users should refer to the{' '}
                <Link to="/privacy/gdpr" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  GDPR Addendum
                </Link>
                {' '}for specific local provisions.
              </p>
            </section>

          </div>
          
          <div className="p-6 md:p-8 bg-[#161513]/70 border-t border-[#0CB4E4]/20">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-4">
                <Link to="/privacy" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  View Privacy Policy
                </Link>
              </div>
              
              <button 
                onClick={() => navigate('/')} 
                className="px-5 py-2.5 bg-[#0C5484] text-white hover:bg-[#0CB4E4]/90 transition-colors shadow-md rounded-lg"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>

        <footer className="text-center text-gray-400 mt-8">
          <p>© Ydrive Inc.</p>
        </footer>
      </div>
    </div>
  );
};

export default TermsPage;